import React, { Children, Fragment, useMemo } from 'react';
import { ActivityIndicator, Platform, Pressable, StyleSheet, View } from 'react-native';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';

const ROW_INSET = 16;
const ICON_TILE = 30;

export const useBillingPalette = () => {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  return useMemo(() => ({
    colors,
    isDark,
    groupFill: isDark ? 'rgba(255,255,255,0.045)' : 'rgba(0,0,0,0.024)',
    groupBorder: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
    separator: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)',
    hover: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
    pressed: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)',
    tileFill: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
    ink: isDark ? '#f5f5f7' : '#1d1d1f',
    inkInverse: isDark ? '#1d1d1f' : '#ffffff',
    destructive: colors?.color_destructive || '#FF3B30',
  }), [colors, isDark]);
};

const createStyles = palette => StyleSheet.create({
  section: {
    gap: 6,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    paddingHorizontal: 4,
  },
  footer: {
    paddingHorizontal: 4,
  },
  group: {
    borderRadius: 14,
    overflow: 'hidden',
    backgroundColor: palette.groupFill,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: palette.groupBorder,
  },
  separator: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: palette.separator,
  },
  row: {
    minHeight: 44,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: ROW_INSET,
    paddingVertical: 9,
  },
  rowCentered: {
    justifyContent: 'center',
  },
  tile: {
    width: ICON_TILE,
    height: ICON_TILE,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: palette.tileFill,
  },
  rowBody: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  rowTrailing: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  valueRow: {
    minHeight: 38,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    paddingHorizontal: ROW_INSET,
    paddingVertical: 8,
  },
});

const useStyles = () => {
  const palette = useBillingPalette();
  return { palette, styles: useMemo(() => createStyles(palette), [palette]) };
};

/** Inset grouped list; children are separated by hairlines. */
export function BillingGroup({ children, inset = 0, style }) {
  const { styles } = useStyles();
  const rows = Children.toArray(children).filter(Boolean);
  if (!rows.length) return null;
  return (
    <View style={[styles.group, style]}>
      {rows.map((row, index) => (
        <Fragment key={row.key ?? index}>
          {index > 0 && <View style={[styles.separator, { marginLeft: ROW_INSET + inset }]} />}
          {row}
        </Fragment>
      ))}
    </View>
  );
}

BillingGroup.propTypes = {
  children: PropTypes.node,
  inset: PropTypes.number,
  style: PropTypes.any,
};

/** Titled section with an optional trailing header accessory and a footnote. */
export function BillingSection({ title, accessory, footer, children }) {
  const { styles } = useStyles();
  return (
    <View style={styles.section}>
      {(title || accessory) && (
        <View style={styles.header}>
          {title ? <Text size="n" weight="semibold" color="primary">{title}</Text> : <View />}
          {accessory}
        </View>
      )}
      {children}
      {footer ? (
        <View style={styles.footer}>
          <Text size="xs" color="secondary">{footer}</Text>
        </View>
      ) : null}
    </View>
  );
}

BillingSection.propTypes = {
  title: PropTypes.node,
  accessory: PropTypes.node,
  footer: PropTypes.node,
  children: PropTypes.node,
};

/** Rounded glyph tile used at the leading edge of list rows. */
export function BillingTile({ icon, color, backgroundColor, size = ICON_TILE, radius = 8, iconSize = 13, solid }) {
  const { palette, styles } = useStyles();
  return (
    <View style={[styles.tile, { width: size, height: size, borderRadius: radius }, backgroundColor && { backgroundColor }]}>
      <Icon name={icon} size={iconSize} color={color || palette.ink} solid={solid} />
    </View>
  );
}

BillingTile.propTypes = {
  icon: PropTypes.string.isRequired,
  color: PropTypes.string,
  backgroundColor: PropTypes.string,
  size: PropTypes.number,
  radius: PropTypes.number,
  iconSize: PropTypes.number,
  solid: PropTypes.bool,
};

/** Label on the leading edge, value on the trailing edge. */
export function BillingValueRow({ label, value, valueColor = 'primary', valueWeight = 'medium' }) {
  const { styles } = useStyles();
  return (
    <View style={styles.valueRow}>
      <Text size="n" color="secondary">{label}</Text>
      <Text size="n" color={valueColor} weight={valueWeight}>{value}</Text>
    </View>
  );
}

BillingValueRow.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.node,
  valueColor: PropTypes.string,
  valueWeight: PropTypes.string,
};

/**
 * Standard list row. Pressable when `onPress` is set; `destructive` renders a
 * centered red action in the style of iOS "Cancel Subscription".
 */
export function BillingRow({
  icon,
  leading,
  title,
  subtitle,
  trailing,
  onPress,
  disabled,
  isLoading,
  chevron,
  destructive,
  testID,
  accessibilityLabel,
}) {
  const { palette, styles } = useStyles();
  const content = destructive ? (
    <Text size="n" weight="medium" style={{ color: palette.destructive }}>{title}</Text>
  ) : (
    <>
      {leading || (icon ? <BillingTile icon={icon} /> : null)}
      <View style={styles.rowBody}>
        {typeof title === 'string'
          ? <Text size="n" weight="medium" color="primary" numberOfLines={1}>{title}</Text>
          : title}
        {subtitle ? (typeof subtitle === 'string'
          ? <Text size="s" color="secondary">{subtitle}</Text>
          : subtitle) : null}
      </View>
      {(trailing || chevron || isLoading) && (
        <View style={styles.rowTrailing}>
          {trailing}
          {isLoading
            ? <ActivityIndicator size="small" color={palette.colors?.text_color_secondary} />
            : chevron && <Icon name="chevron-right" size={11} color={palette.colors?.text_color_tertiary} />}
        </View>
      )}
    </>
  );

  if (!onPress) {
    return <View style={[styles.row, destructive && styles.rowCentered]} testID={testID}>{content}</View>;
  }

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      testID={testID}
      onPress={onPress}
      disabled={disabled || isLoading}
      style={({ hovered, pressed }) => [
        styles.row,
        destructive && styles.rowCentered,
        Platform.OS === 'web' && { cursor: disabled ? 'default' : 'pointer', transition: 'background-color 120ms ease' },
        hovered && { backgroundColor: palette.hover },
        pressed && { backgroundColor: palette.pressed },
        (disabled || isLoading) && { opacity: 0.55 },
      ]}
    >
      {content}
    </Pressable>
  );
}

BillingRow.propTypes = {
  icon: PropTypes.string,
  leading: PropTypes.node,
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  trailing: PropTypes.node,
  onPress: PropTypes.func,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  chevron: PropTypes.bool,
  destructive: PropTypes.bool,
  testID: PropTypes.string,
  accessibilityLabel: PropTypes.string,
};

export const BILLING_ROW_ICON_INSET = ICON_TILE + 12;
